import { Injectable } from '@jkhy/frontend-utils/di';
import {
    HolidayCalendarModelDto,
    HolidayCalendarsClient,
    ProcessingTimesClient,
    TimeZoneModelDto,
} from '@treasury/api/bo';
import { HolidayException } from './entities/holiday-exception.entity';
import { ProcessingTime } from './entities/processing-time.entity';
import { TimeZone } from './entities/time-zone.entity';

@Injectable()
export class ProcessingConfigurationService {
    constructor(
        private readonly processingTimesClient: ProcessingTimesClient,
        private readonly holidayCalendarsClient: HolidayCalendarsClient
    ) {}

    public async getProcessingTimes(): Promise<{
        processingTimes: ProcessingTime[];
        timeZone: TimeZone;
    }> {
        const response = await this.processingTimesClient.processingTimesGet();
        const dto = response.data;
        return {
            processingTimes: (dto.productFeatureProcessingTimes ?? []).map(ProcessingTime.from),
            timeZone: TimeZone.from(dto.timeZone ?? {}),
        };
    }

    public async updateProcessingTime(processingTime: ProcessingTime): Promise<ProcessingTime> {
        const response = await this.processingTimesClient.processingTimesPut(
            processingTime.id,
            processingTime.toDto()
        );
        return ProcessingTime.from(response.data);
    }

    public async getTimeZones(): Promise<TimeZoneModelDto[]> {
        const response = await this.processingTimesClient.processingTimesGetTimeZones();
        return response.data ?? [];
    }

    public async updateTimeZone(timeZone: TimeZone): Promise<void> {
        await this.processingTimesClient.processingTimesUpdateTimeZone(
            timeZone.toDto() as TimeZoneModelDto
        );
    }

    public async getHolidayExceptions(): Promise<HolidayException[]> {
        const response = await this.holidayCalendarsClient.holidayCalendarsGet();
        return (response.data ?? []).map(HolidayException.from);
    }

    public async createHolidayException(holiday: HolidayException): Promise<HolidayException> {
        const response = await this.holidayCalendarsClient.holidayCalendarsPost(
            holiday.toDto() as HolidayCalendarModelDto
        );
        return HolidayException.from(response.data);
    }

    public async updateHolidayException(holiday: HolidayException): Promise<HolidayException> {
        const response = await this.holidayCalendarsClient.holidayCalendarsPut(
            holiday.id,
            holiday.toDto() as HolidayCalendarModelDto
        );
        return HolidayException.from(response.data);
    }

    public async deleteHolidayException(id: number): Promise<void> {
        await this.holidayCalendarsClient.holidayCalendarsDelete(id);
    }
}
